import {dotRadius,pinGrowth,pinTranslation,DOT_LAYER_IDS} from './map-geometry.mjs';

const pointerTypes = new WeakMap();
export function coarsePointer(event, canvas = null) {
  if (event?.pointerType) return event.pointerType !== 'mouse';
  if (event?.sourceCapabilities?.firesTouchEvents || event?.type?.startsWith('touch')) return true;
  if (canvas && pointerTypes.has(canvas)) return pointerTypes.get(canvas) !== 'mouse';
  if (event?.type?.startsWith('mouse')) return false;
  return globalThis.matchMedia?.('(pointer: coarse)').matches ?? false;
}
export function touchPointer(event, canvas = null) {
  if (event?.sourceCapabilities?.firesTouchEvents || event?.type?.startsWith('touch')) return true;
  if (event?.pointerType) return event.pointerType === 'touch';
  return pointerTypes.get(canvas) === 'touch';
}
export const insidePin = (dx,dy) => Math.hypot(dx,dy+31)<=19.1 || (dy>=-20 && dy<=1 && Math.abs(dx)<=(1-dy)*16/21);
// CSS pixels: a dot target is exactly 44 px across, independent of density/zoom.
export const TOUCH_RADIUS = 22;
const inBox = (x, y, halfWidth, halfHeight) => Math.abs(x) <= halfWidth && Math.abs(y) <= halfHeight;
function segmentDistance(x, y, ax, ay, bx, by) {
  const t = Math.max(0, Math.min(1, ((x-ax)*(bx-ax)+(y-ay)*(by-ay))/((bx-ax)**2+(by-ay)**2)));
  return Math.hypot(x-ax-t*(bx-ax), y-ay-t*(by-ay));
}
function onShape(feature, dx, dy, zoom, coarse) {
  const growth = feature.layer?.id === 'points' ? pinGrowth(zoom) : 1;
  const scale = (feature.properties.pinScale || 1) * growth;
  const y = dy - (feature.layer?.id === 'points' ? pinTranslation(zoom) : 0);
  if (feature.layer?.id === 'hit-labels') return inBox(dx+29, dy+31, coarse ? 22 : 16, coarse ? 22 : 16);
  if (!feature.properties.pinSprite) return Math.hypot(dx, dy) <= (coarse ? Math.max(TOUCH_RADIUS, dotRadius(zoom)+1) : dotRadius(zoom)+1);
  if (insidePin(dx/scale, y/scale)) return true;
  if (coarse) {
    // Expand the round head only to 44 px; keep the tip's triangular outline.
    const pad = Math.max(0, TOUCH_RADIUS-19.1*scale);
    if (Math.hypot(dx, y+31*scale) <= 19.1*scale+pad) return true;
    for (const [ax,ay,bx,by] of [[-16,-20,0,1],[0,1,16,-20],[-16,-20,16,-20]])
      if (segmentDistance(dx,y,ax*scale,ay*scale,bx*scale,by*scale) <= pad) return true;
  }
  // Rank tiles sit left of the head; power/price badges sit below the geographic tip.
  const halfWidth = feature.properties.pinBadgeWidth*growth/2+scale;
  return !!feature.properties.pinBadge && inBox(dx, y-13.5*scale,
    coarse ? Math.max(22,halfWidth) : halfWidth, coarse ? Math.max(22,9.5*scale) : 9.5*scale);
}
export function nearestHit(map, point, coarse = false) {
  const zoom = map.getZoom?.() ?? 15;
  const gpu = map._w2cGpu;
  const layers = [...(gpu ? [] : DOT_LAYER_IDS), 'points', 'hit-points', 'hit-labels'].filter(id => map.getLayer(id));
  if (!layers.length && !gpu) return null;
  const found = [...(layers.length ? map.queryRenderedFeatures([[point.x - 24, point.y - 24], [point.x + 24, point.y + 24]], {layers}) : []), ...(gpu?.hits(map, point) ?? [])];
  let nearest = null, distance = Infinity;
  for (const feature of found) {
    const p = map.project(feature.geometry.coordinates), dx = point.x - p.x, dy = point.y - p.y;
    const d = Math.hypot(dx, dy);
    // GPU hits are grid-cell candidates, not bounded screen-space hits.
    if (!onShape(feature, dx, dy, zoom, coarse)) continue;
    if (d < distance) { nearest = feature; distance = d; }
  }
  return nearest;
}
export function installPointer(map) {
  const canvas = map.getCanvas(); let dragging = false;
  for (const type of ['pointermove', 'pointerdown']) canvas.addEventListener(type, event => pointerTypes.set(canvas, event.pointerType));
  const clear = () => { canvas.style.cursor = ''; };
  map.on('mousemove', event => { canvas.style.cursor = !dragging && nearestHit(map, event.point, coarsePointer(event.originalEvent, canvas)) ? 'pointer' : ''; });
  map.on('dragstart', () => { dragging = true; clear(); });
  map.on('dragend', () => { dragging = false; clear(); });
  map.on('style.load', clear);
  canvas.addEventListener('mouseleave', clear);
}
